import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SessionManagement = () => {
  const [sessions, setSessions] = useState([
    {
      id: 1,
      device: "MacBook Pro 16-inch",
      browser: "Chrome 118.0.5993.88",
      location: "New York, NY, USA",
      ipAddress: "192.168.1.45",
      loginTime: "2024-10-29T14:30:00Z",
      lastActivity: "2024-10-29T16:45:00Z",
      status: "active",
      isCurrent: true,
      os: "macOS 14.1"
    },
    {
      id: 2,
      device: "iPhone 15 Pro",
      browser: "Safari Mobile 17.1",
      location: "New York, NY, USA",
      ipAddress: "192.168.1.67",
      loginTime: "2024-10-29T09:15:00Z",
      lastActivity: "2024-10-29T15:22:00Z",
      status: "active",
      isCurrent: false,
      os: "iOS 17.1"
    },
    {
      id: 3,
      device: "Windows Desktop",
      browser: "Edge 118.0.2088.46",
      location: "London, UK",
      ipAddress: "203.0.113.42",
      loginTime: "2024-10-28T11:20:00Z",
      lastActivity: "2024-10-28T17:30:00Z",
      status: "expired",
      isCurrent: false,
      os: "Windows 11"
    },
    {
      id: 4,
      device: "iPad Air",
      browser: "Safari Mobile 17.0",
      location: "San Francisco, CA, USA",
      ipAddress: "198.51.100.23",
      loginTime: "2024-10-27T16:45:00Z",
      lastActivity: "2024-10-27T18:12:00Z",
      status: "inactive",
      isCurrent: false,
      os: "iPadOS 17.0"
    }
  ]);

  const terminateSession = (sessionId) => {
    setSessions(sessions?.map(session => 
      session?.id === sessionId 
        ? { ...session, status: 'terminated' }
        : session
    ));
  };

  const terminateAllOtherSessions = () => {
    setSessions(sessions?.map(session => 
      session?.isCurrent 
        ? session 
        : { ...session, status: 'terminated' }
    ));
  };

  const getDeviceIcon = (device) => {
    if (device?.toLowerCase()?.includes('iphone')) return 'Smartphone';
    if (device?.toLowerCase()?.includes('ipad')) return 'Tablet';
    if (device?.toLowerCase()?.includes('macbook') || device?.toLowerCase()?.includes('mac')) return 'Laptop';
    if (device?.toLowerCase()?.includes('windows') || device?.toLowerCase()?.includes('desktop')) return 'Monitor';
    return 'Device';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'inactive': return 'text-warning';
      case 'expired': return 'text-muted-foreground';
      case 'terminated': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return 'bg-success/10 text-success';
      case 'inactive': return 'bg-warning/10 text-warning';
      case 'expired': return 'bg-muted text-muted-foreground';
      case 'terminated': return 'bg-destructive/10 text-destructive';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h ago`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)}d ago`;
    }
  };

  const activeSessions = sessions?.filter(session => session?.status === 'active')?.length;
  const totalSessions = sessions?.length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Session Management</h3>
          <p className="text-sm text-text-secondary mt-1">
            Monitor and manage your active login sessions across devices
          </p>
        </div>
        <Button 
          variant="destructive" 
          onClick={terminateAllOtherSessions}
          iconName="LogOut"
          iconPosition="left"
        >
          Terminate All Others
        </Button>
      </div>
      {/* Session Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="Activity" size={20} className="text-success" />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-card-foreground">{activeSessions}</h4>
              <p className="text-sm text-text-secondary">Active Sessions</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Monitor" size={20} className="text-primary" />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-card-foreground">{totalSessions}</h4>
              <p className="text-sm text-text-secondary">Total Sessions</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="MapPin" size={20} className="text-warning" />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-card-foreground">3</h4>
              <p className="text-sm text-text-secondary">Locations</p>
            </div>
          </div>
        </div>
      </div>
      {/* Active Sessions List */}
      <div className="space-y-4">
        <h4 className="text-md font-semibold text-foreground">Active Sessions</h4>
        
        {sessions?.length === 0 ? (
          <div className="text-center py-12 bg-muted rounded-lg">
            <Icon name="Monitor" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">No Active Sessions</h4>
            <p className="text-text-secondary">
              You don't have any active sessions at the moment
            </p>
          </div>
        ) : (
          sessions?.map((session) => (
            <div key={session?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={getDeviceIcon(session?.device)} size={24} className="text-primary" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h5 className="text-md font-semibold text-card-foreground">{session?.device}</h5>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(session?.status)}`}>
                        {session?.status}
                      </span>
                      {session?.isCurrent && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-accent/10 text-accent">
                          Current
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-text-secondary">Browser:</span>
                        <p className="text-card-foreground font-medium">{session?.browser}</p>
                      </div>
                      <div>
                        <span className="text-text-secondary">Operating System:</span>
                        <p className="text-card-foreground font-medium">{session?.os}</p>
                      </div>
                      <div>
                        <span className="text-text-secondary">Location:</span>
                        <p className="text-card-foreground font-medium">{session?.location}</p>
                      </div>
                      <div>
                        <span className="text-text-secondary">IP Address:</span>
                        <p className="text-card-foreground font-medium font-mono">{session?.ipAddress}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mt-3">
                      <div>
                        <span className="text-text-secondary">Login Time:</span>
                        <p className="text-card-foreground font-medium">
                          {new Date(session.loginTime)?.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <span className="text-text-secondary">Last Activity:</span>
                        <p className="text-card-foreground font-medium">
                          {formatTimeAgo(session?.lastActivity)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {session?.status === 'active' && !session?.isCurrent && (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => terminateSession(session?.id)}
                    >
                      <Icon name="LogOut" size={16} />
                    </Button>
                  )}
                  <Button variant="outline" size="sm">
                    <Icon name="MapPin" size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {/* Security Recommendations */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-semibold text-card-foreground mb-4 flex items-center gap-2">
          <Icon name="Shield" size={20} className="text-primary" />
          Security Recommendations
        </h4>
        
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-warning/5 border border-warning/20 rounded-lg">
            <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
            <div>
              <h5 className="font-medium text-card-foreground mb-1">Unrecognized Location</h5>
              <p className="text-sm text-text-secondary">
                We detected a login from London, UK. If this wasn't you, please terminate that session and change your password.
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h5 className="font-medium text-card-foreground mb-1">Enable Two-Factor Authentication</h5>
              <p className="text-sm text-text-secondary">
                Add an extra layer of security to your account by enabling 2FA in your security settings.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionManagement;